// Tunings for the tuner: each instrument's standard tuning, common
// alternatives, and the user's own saved tunings.
//
// A tuning is { id, name, notes, labels, custom } where notes are MIDI numbers,
// low string first, and labels are how each string's note is spelled.

import { centsBetween, midiToFrequency, midiToNote, parseNote } from './pitch'

// Range a custom tuning's strings can be set to: a 5-string bass's low B up
// to well past a mandolin's high E.
export const MIN_NOTE = parseNote('B0')
export const MAX_NOTE = parseNote('E6')
export const MAX_NAME_LENGTH = 40
const MAX_STRINGS = 12

// Open-string notes keyed by Instrument.safe_name. The instrument model only
// stores letters (e.g. "GCEA"), which says nothing about octaves or re-entrant
// tunings, so known instruments are spelled out.
const STANDARD_TUNINGS = {
    guitar: ['E2', 'A2', 'D3', 'G3', 'B3', 'E4'],
    ukulele: ['G4', 'C4', 'E4', 'A4'],
    bass: ['E1', 'A1', 'D2', 'G2'],
    bass_basic: ['E1', 'A1', 'D2', 'G2'],
    guitalele: ['A2', 'D3', 'G3', 'C4', 'E4', 'A4'],
    mandolin: ['G3', 'D4', 'A4', 'E5'],
    banjo: ['C3', 'G3', 'B3', 'D4'],
}

// Common alternative tunings. Names like "Drop D" or "DADGAD" are the same in
// every language, so only the descriptive ones are translated.
const presetTunings = (safeName) => {
    switch (safeName) {
        case 'guitar':
            return [
                { id: 'drop-d', name: 'Drop D', notes: ['D2', 'A2', 'D3', 'G3', 'B3', 'E4'] },
                { id: 'half-step-down', name: gettext('Half step down'), notes: ['Eb2', 'Ab2', 'Db3', 'Gb3', 'Bb3', 'Eb4'] },
                { id: 'whole-step-down', name: gettext('Whole step down'), notes: ['D2', 'G2', 'C3', 'F3', 'A3', 'D4'] },
                { id: 'drop-c', name: 'Drop C', notes: ['C2', 'G2', 'C3', 'F3', 'A3', 'D4'] },
                { id: 'dadgad', name: 'DADGAD', notes: ['D2', 'A2', 'D3', 'G3', 'A3', 'D4'] },
                { id: 'open-d', name: 'Open D', notes: ['D2', 'A2', 'D3', 'F#3', 'A3', 'D4'] },
                { id: 'open-e', name: 'Open E', notes: ['E2', 'B2', 'E3', 'G#3', 'B3', 'E4'] },
                { id: 'open-g', name: 'Open G', notes: ['D2', 'G2', 'D3', 'G3', 'B3', 'D4'] },
                { id: 'open-c', name: 'Open C', notes: ['C2', 'G2', 'C3', 'G3', 'C4', 'E4'] },
                { id: '7-string', name: gettext('7-string standard'), notes: ['B1', 'E2', 'A2', 'D3', 'G3', 'B3', 'E4'] },
            ]
        case 'ukulele':
            return [
                { id: 'low-g', name: gettext('Low G'), notes: ['G3', 'C4', 'E4', 'A4'] },
                { id: 'd-tuning', name: gettext('D tuning'), notes: ['A4', 'D4', 'F#4', 'B4'] },
                { id: 'baritone', name: gettext('Baritone'), notes: ['D3', 'G3', 'B3', 'E4'] },
            ]
        case 'bass':
        case 'bass_basic':
            return [
                { id: 'drop-d', name: 'Drop D', notes: ['D1', 'A1', 'D2', 'G2'] },
                { id: 'half-step-down', name: gettext('Half step down'), notes: ['Eb1', 'Ab1', 'Db2', 'Gb2'] },
                { id: '5-string', name: gettext('5-string standard'), notes: ['B0', 'E1', 'A1', 'D2', 'G2'] },
            ]
        default:
            return []
    }
}

// Used when the instrument list hasn't been loaded (keeps the tuner usable).
export const DEFAULT_TUNER_INSTRUMENTS = [
    { id: 'guitar', name: 'Guitar', safe_name: 'guitar', tuning: 'EADGBE' },
    { id: 'ukulele', name: 'Ukulele', safe_name: 'ukulele', tuning: 'GCEA' },
    { id: 'bass', name: 'Bass', safe_name: 'bass', tuning: 'EADG' },
    { id: 'guitalele', name: 'Guitalele', safe_name: 'guitalele', tuning: 'ADGCEA' },
]

const stripOctave = (note) => note.replace(/-?\d$/, '')

const fromNoteNames = (notes) => ({
    notes: notes.map(parseNote),
    labels: notes.map(stripOctave),
})

// "EADGBE" -> ['E', 'A', ...]; also accepts accidentals such as "DADF#AD".
const splitTuning = (tuning) => (tuning || '').match(/[A-G][#b]?/g) || []

const pitchClass = (name) => ((parseNote(`${name}4`) % 12) + 12) % 12

// The instrument's standard tuning as { notes, labels }, or null if it has no
// strings to tune (e.g. piano).
const standardTuning = (instrument) => {
    const letters = splitTuning(instrument && instrument.tuning)
    if (letters.length < 2) {
        return null
    }

    const known = STANDARD_TUNINGS[instrument.safe_name]
    if (known && known.length === letters.length &&
        known.every((note, i) => pitchClass(stripOctave(note)) === pitchClass(letters[i]))) {
        return fromNoteNames(known)
    }

    // Unknown or re-tuned instrument: stack each string above the previous
    // one, starting from the second octave like a guitar.
    const notes = []
    letters.forEach((letter, i) => {
        const pc = pitchClass(letter)
        if (i === 0) {
            notes.push(pc + 36)
        } else {
            let midi = notes[i - 1] - (notes[i - 1] % 12) + pc
            if (midi <= notes[i - 1]) {
                midi += 12
            }
            notes.push(midi)
        }
    })
    return { notes, labels: letters }
}

export const isTunable = (instrument) => standardTuning(instrument) !== null

// Saved tunings come back from the user's profile as plain JSON; ignore any
// that don't look right rather than breaking the tuner.
const isValidCustomTuning = (tuning) => !!tuning &&
    typeof tuning.id === 'string' &&
    typeof tuning.name === 'string' &&
    Array.isArray(tuning.notes) &&
    tuning.notes.length >= 1 && tuning.notes.length <= MAX_STRINGS &&
    tuning.notes.every(n => Number.isInteger(n) && n >= MIN_NOTE && n <= MAX_NOTE)

export const labelsForNotes = (notes) => notes.map(n => midiToNote(n).name)

// Every tuning offered for an instrument: standard first, then presets, then
// the user's own.
export const tuningsForInstrument = (instrument, customTunings) => {
    const standard = standardTuning(instrument)
    if (!standard) {
        return []
    }
    const own = (customTunings || [])
        .filter(t => isValidCustomTuning(t) && t.instrument === instrument.safe_name)
        .map(t => ({ id: t.id, name: t.name, notes: t.notes, labels: labelsForNotes(t.notes), custom: true }))

    return [
        { id: 'standard', name: gettext('Standard'), ...standard },
        ...presetTunings(instrument.safe_name).map(p => ({ id: p.id, name: p.name, ...fromNoteNames(p.notes) })),
        ...own,
    ]
}

// A user tuning as it's stored in their profile.
export const makeCustomTuning = (id, instrument, name, notes) => ({
    id: id || `custom-${Date.now().toString(36)}`,
    instrument: instrument.safe_name,
    name: name.trim().slice(0, MAX_NAME_LENGTH),
    notes,
})

export const stringsForTuning = (tuning, a4) => tuning.notes.map((midi, i) => ({
    label: tuning.labels[i],
    midi,
    frequency: midiToFrequency(midi, a4),
}))

// The string a detected frequency is most likely meant to be.
export const closestString = (strings, frequency) => {
    let best = -1
    let bestDistance = Infinity
    strings.forEach((string, i) => {
        const distance = Math.abs(centsBetween(frequency, string.frequency))
        if (distance < bestDistance) {
            best = i
            bestDistance = distance
        }
    })
    return best
}
